'use client';

import { faEnvelope, faPhone } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@pickleballinc/react-ui';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Button } from '@/components/atoms';
import { TeamPlayerRegistrationData, teamPlayerRegistrationSchema } from '@/entities';

interface TeamPlayerRegistrationFormProps {
	email: string;
	phone: string;
}

interface ITeamDetails {
	teamName: string;
	teamCaptain: string;
}

export const TeamPlayerRegistrationForm = ({ email, phone }: TeamPlayerRegistrationFormProps) => {
	const [teamDetails, setTeamDetails] = useState<ITeamDetails>();
	const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

	const form = useForm<TeamPlayerRegistrationData>({
		resolver: zodResolver(teamPlayerRegistrationSchema)
	});

	const { formState, register, handleSubmit } = form;

	const onSubmit = (_data: TeamPlayerRegistrationData) => {
		if (!isSubmitted) {
			// Dummy data
			setTeamDetails({
				teamName: 'Super Pickles',
				teamCaptain: 'Nancy Kaplan'
			});

			setIsSubmitted(true);
		} else {
			// API needs to be implemented.
		}
	};
	return (
		<FormProvider {...form}>
			<form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-9" autoComplete="off">
				<div className="flex flex-col gap-4 sm:flex-row">
					<InputField
						focusColor="orange"
						{...register('email')}
						label="Email*"
						defaultValue={email}
						hasError={!!formState.errors.email}
						hint={formState.errors.email?.message?.toString()}
						className="border-none text-sm font-medium leading-5 outline-none"
						inputProps={{
							labelClassName: `text-sm mb-1.5 text-gray-700`,
							className: 'text-base font-normal leading-6 text-gray-900 py-0 px-2'
						}}
						PrefixIcon={() => <FontAwesomeIcon icon={faEnvelope} size="lg" className="size-5" />}
					/>
					<InputField
						focusColor="orange"
						{...register('phoneNumber')}
						label="Phone*"
						defaultValue={phone}
						hasError={!!formState.errors.phoneNumber}
						hint={formState.errors.phoneNumber?.message?.toString()}
						className="border-none text-sm font-medium leading-5 outline-none"
						inputProps={{
							labelClassName: `text-sm mb-1.5 text-gray-700`,
							className: 'text-base font-normal leading-6 text-gray-900 py-0 px-2'
						}}
						PrefixIcon={() => <FontAwesomeIcon icon={faPhone} size="lg" className="size-5" />}
					/>
				</div>

				<div>
					<InputField
						focusColor="orange"
						{...register('invitationCode')}
						label="Enter team invitation code"
						hasError={!!formState.errors.invitationCode}
						hint={formState.errors.invitationCode?.message?.toString()}
						className="flex items-center self-stretch rounded-lg border-solid sm:w-[340px]"
						inputProps={{
							labelClassName: `text-base font-semibold leading-6 text-brand-800 mb-4`,
							className: 'px-3 py-2 bg-white text-gray-500 rounded-lg h-10 text-base font-normal leading-6'
						}}
					/>

					{formState.isValid && formState.isSubmitted && (
						<div className="mt-6 flex flex-col gap-4">
							<div>
								<span className="text-sm font-medium leading-5 text-gray-700">Team</span>
								<p className="px-3.5 py-2.5 text-base font-normal leading-6 text-gray-500">{teamDetails?.teamName}</p>
							</div>
							<div>
								<span className="text-sm font-medium leading-5 text-gray-700">Team captain</span>
								<p className="px-3.5 py-2.5 text-base font-normal leading-6 text-gray-500">{teamDetails?.teamCaptain}</p>
							</div>
						</div>
					)}
				</div>

				<div className="flex gap-4">
					<Button type="button" className="bg-white !text-black">
						Back
					</Button>
					<Button type="submit" disabled={form.formState.isSubmitting || !form.formState.isDirty}>
						{formState.isValid && formState.isSubmitted ? 'Join team' : 'Submit'}
					</Button>
				</div>
			</form>
		</FormProvider>
	);
};
